/**
 * @fileoverview Week View Component for Room-Centric Planner
 * @module features/coordinator/planner/WeekView
 * @description 7-day week grid showing room assignments across session times
 * @created 10-10-2025 20:55:00
 * @author MCHD Room Scheduler Team
 */

import React from 'react';
import {
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  ScrollArea,
  Grid,
  Card,
  Tooltip,
  ActionIcon,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconMapPin,
  IconClock,
  IconUser,
  IconPlus,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { getWeekDates, formatDayShort, isDateToday, isDatePast, enrichCellStatusAssignment } from './utils/calendarHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface SessionTemplate {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  floorId: string;
  floor: {
    name: string;
    level: number;
    building: {
      name: string;
    };
  };
}

export interface Assignment {
  id: string;
  title: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  conflictCode?: string;
  // FIXED: 15-10-2025 19:30:00 - Add nested data from backend API
  user?: {
    id: string;
    name: string;
    role: string;
  };
  room?: {
    id: string;
    number: string;
    name: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  // Legacy support
  users?: {
    id: string;
    name: string;
  };
}

export interface CellStatus {
  roomId: string;
  sessionTemplateId: string;
  date: string;
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment;
  conflicts?: string[];
  holiday?: any;
}

export interface WeekViewProps {
  /** Current viewing date */
  currentDate: Date;
  
  /** Rooms to display */
  rooms: Room[];
  
  /** Session templates/times */
  sessionTemplates: SessionTemplate[];
  
  /** Cell statuses data from API */
  cellStatuses: CellStatus[];
  
  /** Full assignments array with nested data */
  assignments?: Assignment[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Callback when cell is clicked for assignment creation */
  onCellClick?: ((roomId: string, sessionId: string, date: Date) => void) | undefined;
  
  /** Callback when assignment is clicked */
  onAssignmentClick?: ((assignment: Assignment) => void) | undefined;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get cell status for specific room, session, and date
 * ENHANCED: 15-10-2025 17:50:00 - Extract assignment data from cellStatuses
 */
function getCellStatus(
  cellStatuses: CellStatus[],
  roomId: string,
  sessionId: string,
  date: Date
): CellStatus | undefined {
  const dateString = date.toISOString().split('T')[0];
  const cellStatus = cellStatuses.find(
    (cs) =>
      cs.roomId === roomId &&
      cs.sessionTemplateId === sessionId &&
      cs.date === dateString
  );
  
  // If cellStatus exists but doesn't have assignment data, try to find it
  if (cellStatus && cellStatus.status === 'occupied' && !cellStatus.assignment) {
    // Look for assignment data in the cellStatuses array
    // This handles cases where assignment data is stored separately
    const assignmentData = cellStatuses.find(
      (cs) => 
        cs.roomId === roomId && 
        cs.sessionTemplateId === sessionId && 
        cs.date === dateString &&
        cs.assignment
    );
    
    if (assignmentData?.assignment) {
      return {
        ...cellStatus,
        assignment: assignmentData.assignment
      };
    }
  }
  
  return cellStatus;
}

/**
 * Get display info for cell based on status
 */
function getCellDisplayInfo(cellStatus?: CellStatus) {
  if (!cellStatus) {
    return { text: 'Available', color: 'gray', bgColor: '#f8f9fa', icon: '' };
  }

  switch (cellStatus.status) {
    case 'occupied':
      // Check assignment status for proper display
      if (cellStatus.assignment) {
        switch (cellStatus.assignment.status) {
          case 'CONFIRMED':
            return {
              text: 'Assigned',
              color: 'green',
              bgColor: '#d1fae5',
              icon: '✓',
            };
          case 'CANCELLED':
            return {
              text: 'Cancelled',
              color: 'gray',
              bgColor: '#f3f4f6',
              icon: '✕',
            };
          default:
            return {
              text: 'Assigned',
              color: 'green',
              bgColor: '#d1fae5',
              icon: '✓',
            };
        }
      }
      // Fallback if no assignment data
      return {
        text: 'Assigned',
        color: 'green',
        bgColor: '#d1fae5',
        icon: '✓',
      };
    case 'blocked':
      return {
        text: 'Blocked',
        color: 'red',
        bgColor: '#fee2e2',
        icon: '🚫',
      };
    case 'conflict':
      return {
        text: 'Conflict',
        color: 'orange',
        bgColor: '#fff4e6',
        icon: '⚠️',
      };
    case 'holiday':
      return {
        text: 'Holiday',
        color: 'purple',
        bgColor: '#faf5ff',
        icon: '🎉',
      };
    case 'inactive':
      return {
        text: 'Inactive',
        color: 'gray',
        bgColor: '#f3f4f6',
        icon: '',
      };
    case 'empty':
    default:
      return {
        text: 'Available',
        color: 'green',
        bgColor: '#f0f9ff',
        icon: '',
      };
  }
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Assignment Card Component with Rich Tooltips
 */
interface AssignmentCardProps {
  assignment: Assignment;
  onClick?: (() => void) | undefined;
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({ 
  assignment, 
  onClick
}) => {
  const hasConflict = !!assignment.conflictCode;

  // FIXED: 15-10-2025 19:30:00 - Use correct nested properties from backend API
  // Backend returns: user, room, sessionTime (not users, rooms, session_times)
  const roomData = assignment.room;
  const sessionData = assignment.sessionTime;
  const doctorData = assignment.user || assignment.users; // Support both new and legacy

  // Rich tooltip content
  const tooltipContent = (
    <Stack gap={4}>
      <Text size="xs" fw={600}>{assignment.title || 'Untitled Assignment'}</Text>
      <Text size="xs">Doctor: {doctorData?.name || 'Unknown Doctor'}</Text>
      <Text size="xs">Room: {roomData?.number || 'Unknown'} - {roomData?.name || 'Unknown Room'}</Text>
      <Text size="xs">Session: {sessionData?.name || 'Unknown Session'} ({sessionData?.startTime || 'Unknown'} - {sessionData?.endTime || 'Unknown'})</Text>
      <Text size="xs">Status: {assignment.status}</Text>
      <Text size="xs">Date: {new Date(assignment.date).toLocaleDateString()}</Text>
      {assignment.published === false && <Text size="xs" c="orange">Unpublished</Text>}
      {hasConflict && <Text size="xs" c="red">Conflict: {assignment.conflictCode}</Text>}
    </Stack>
  );

  return (
    <Tooltip
      label={tooltipContent}
      multiline
      withArrow
      position="top"
      openDelay={300}
    >
      <Card
        p="xs"
        radius="sm"
        withBorder
        style={{
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: hasConflict ? '#fff4e6' : '#d1fae5',
          borderColor: hasConflict ? '#fb923c' : '#10b981',
        }}
        onClick={onClick}
      >
        <Stack gap={4}>
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text 
              size="xs" 
              fw={600} 
              truncate
              style={{ 
                flex: 1, 
                minWidth: 0,
                lineHeight: 1.2 
              }}
            >
              {assignment.title || 'Untitled'}
            </Text>
            {hasConflict && (
              <IconAlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0 }} />
            )}
          </Group>
          
          {doctorData && (
            <Group gap={4}>
              <IconUser size={12} color="gray" />
              <Text size="xs" c="dimmed" truncate>
                {doctorData.name}
              </Text>
            </Group>
          )}
          
          {assignment.published === false && (
            <Badge size="xs" color="orange" variant="light">
              Unpublished
            </Badge>
          )}
        </Stack>
      </Card>
    </Tooltip>
  );
};

/**
 * Cell Component
 */
interface CellComponentProps {
  cellStatus?: CellStatus | undefined;
  onCellClick?: (() => void) | undefined;
  onAssignmentClick?: (() => void) | undefined;
  isPast: boolean;
}

const CellComponent: React.FC<CellComponentProps> = ({
  cellStatus,
  onCellClick,
  onAssignmentClick,
  isPast,
}) => {
  const displayInfo = getCellDisplayInfo(cellStatus);
  const hasAssignment = cellStatus?.assignment;

  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      style={{
        minHeight: 80,
        backgroundColor: isPast ? '#f3f4f6' : displayInfo.bgColor,
        cursor: isPast ? 'default' : 'pointer',
        opacity: isPast ? 0.6 : 1,
      }}
      onClick={() => {
        if (isPast) return;
        if (hasAssignment && onAssignmentClick) {
          onAssignmentClick();
        } else if (onCellClick) {
          onCellClick();
        }
      }}
    >
      {hasAssignment ? (
        <AssignmentCard
          assignment={cellStatus!.assignment!}
          onClick={onAssignmentClick}
        />
      ) : (
        <Stack gap="xs" align="center" justify="center" style={{ minHeight: 70 }}>
          <Text size="xs" c="dimmed">
            {displayInfo.icon} {displayInfo.text}
          </Text>
          {!isPast && cellStatus?.status === 'empty' && onCellClick && (
            <ActionIcon
              size="xs"
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onCellClick();
              }}
            >
              <IconPlus size={12} />
            </ActionIcon>
          )}
        </Stack>
      )}
    </Paper>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Week View Component
 * Displays 7-day week grid with rooms and session times
 * 
 * @created 10-10-2025 20:55:00
 */
export const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  rooms,
  sessionTemplates,
  cellStatuses,
  assignments = [],
  isLoading = false,
  onCellClick,
  onAssignmentClick,
}) => {
  // Get week dates (Monday-Sunday)
  const weekDates = getWeekDates(currentDate);

  // ENHANCED: 15-10-2025 17:50:00 - Debug data availability
  console.log('📅 [WeekView] Data Debug:', {
    currentDate: currentDate.toISOString().split('T')[0],
    weekDates: weekDates.map(d => d.toISOString().split('T')[0]),
    roomsCount: rooms.length,
    sessionTemplatesCount: sessionTemplates.length,
    cellStatusesCount: cellStatuses.length,
    cellStatusesWithAssignments: cellStatuses.filter(cs => cs.assignment).length,
    sampleCellStatus: cellStatuses[0]
  });

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (rooms.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Text ta="center" c="dimmed">
          No rooms available for this period
        </Text>
      </Paper>
    );
  }

  return (
    <ScrollArea h={600} type="auto">
      <Stack gap="md">
        {/* Week days header */}
        <Grid gutter="xs" align="stretch">
          <Grid.Col span={2}>
            <Paper p="sm" withBorder bg="gray.1">
              <Text size="sm" fw={600}>
                Room / Day
              </Text>
            </Paper>
          </Grid.Col>
          {weekDates.map((date) => {
            const isToday = isDateToday(date);
            const isPast = isDatePast(date);
            
            return (
              <Grid.Col key={date.toISOString()} span={1.6}>
                <Paper
                  p="sm"
                  withBorder
                  bg={isToday ? 'blue.1' : isPast ? 'gray.0' : 'white'}
                >
                  <Stack gap={2} align="center">
                    <Text size="xs" fw={500} tt="uppercase" c="dimmed">
                      {formatDayShort(date)}
                    </Text>
                    {isToday && (
                      <Badge size="xs" color="blue" variant="filled">
                        Today
                      </Badge>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>
            );
          })}
        </Grid>

        {/* Rooms and their assignments across the week */}
        {rooms.map((room) => (
          <Card key={room.id} p="sm" withBorder>
            <Stack gap="xs">
              {/* Room header */}
              <Group gap="xs" wrap="nowrap">
                <IconMapPin size={16} color="gray" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {room.number} - {room.name}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {room.floor.building.name} - Floor {room.floor.level}
                  </Text>
                </div>
              </Group>

              {/* Session times for this room across the week */}
              {sessionTemplates.map((session) => (
                <div key={session.id}>
                  <Group gap="xs" mb={4}>
                    <IconClock size={14} color="gray" />
                    <Text size="xs" fw={500}>
                      {session.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ({session.startTime} - {session.endTime})
                    </Text>
                  </Group>

                  <Grid gutter="xs">
                    <Grid.Col span={2} />
                    {weekDates.map((date) => {
                      const isPast = isDatePast(date);
                      const cellStatus = getCellStatus(
                        cellStatuses,
                        room.id,
                        session.id,
                        date
                      );
                      const enrichedCellStatus = cellStatus 
                        ? enrichCellStatusAssignment(cellStatus, assignments)
                        : undefined;

                      return (
                        <Grid.Col key={date.toISOString()} span={1.6}>
                          <CellComponent
                            cellStatus={enrichedCellStatus}
                            isPast={isPast}
                            onCellClick={
                              onCellClick
                                ? () => onCellClick(room.id, session.id, date)
                                : undefined
                            }
                            onAssignmentClick={
                              cellStatus?.assignment && onAssignmentClick
                                ? () => onAssignmentClick(cellStatus.assignment!)
                                : undefined
                            }
                          />
                        </Grid.Col>
                      );
                    })}
                  </Grid>
                </div>
              ))}
            </Stack>
          </Card>
        ))}
      </Stack>
    </ScrollArea>
  );
};

export default WeekView;

